import { useEffect, useRef } from "react";
import type { MapRef } from "react-map-gl/maplibre";
import { useEditorStore } from "@/store";
import type { StoreLayer } from "@/store";

const HIGHLIGHT_LAYER_PREFIX = "__editor-highlight-";

/**
 * Manages a temporary highlight overlay layer on the map
 * when hovering layers in the editor.
 */
export function useMapHighlight(mapRef: React.RefObject<MapRef | null>) {
  const hoveredLayerId = useEditorStore((s) => s.hoveredLayerId);
  const layers = useEditorStore((s) => s.style.layers);
  const prevHighlight = useRef<string | null>(null);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !map.isStyleLoaded()) return;

    // Remove previous highlight
    if (prevHighlight.current) {
      try {
        if (map.getLayer(prevHighlight.current)) {
          map.removeLayer(prevHighlight.current);
        }
      } catch {
        // Layer might already be removed
      }
      prevHighlight.current = null;
    }

    if (!hoveredLayerId) return;

    const layer = layers.find((l) => l.id === hoveredLayerId);
    if (!layer) return;

    const highlightId = `${HIGHLIGHT_LAYER_PREFIX}${hoveredLayerId}`;
    prevHighlight.current = highlightId;

    try {
      const highlightDef = createHighlightLayer(highlightId, layer);
      if (highlightDef) {
        map.addLayer(highlightDef as maplibregl.LayerSpecification);
      }
    } catch {
      // Style might be in an invalid state
    }

    return () => {
      try {
        if (map.getLayer(highlightId)) {
          map.removeLayer(highlightId);
        }
      } catch {
        // Cleanup on unmount
      }
    };
  }, [hoveredLayerId, layers, mapRef]);
}

function createHighlightLayer(
  highlightId: string,
  layer: StoreLayer,
): Record<string, unknown> | null {
  const base: Record<string, unknown> = {
    id: highlightId,
    source: layer.source,
  };

  if (layer["source-layer"]) {
    base["source-layer"] = layer["source-layer"];
  }
  if (layer.filter) {
    base.filter = layer.filter;
  }
  if (layer.minzoom !== undefined) base.minzoom = layer.minzoom;
  if (layer.maxzoom !== undefined) base.maxzoom = layer.maxzoom;

  switch (layer.type) {
    case "fill":
      return {
        ...base,
        type: "fill",
        paint: {
          "fill-color": "#0078ff",
          "fill-opacity": 0.25,
        },
      };
    case "line":
      return {
        ...base,
        type: "line",
        paint: {
          "line-color": "#0078ff",
          "line-width": 4,
          "line-opacity": 0.7,
        },
      };
    case "circle":
      return {
        ...base,
        type: "circle",
        paint: {
          "circle-color": "#0078ff",
          "circle-opacity": 0.4,
          "circle-radius": 8,
          "circle-stroke-color": "#0078ff",
          "circle-stroke-width": 2,
        },
      };
    case "symbol":
      return {
        ...base,
        type: "circle",
        paint: {
          "circle-color": "#0078ff",
          "circle-opacity": 0.3,
          "circle-radius": 12,
        },
      };
    case "fill-extrusion":
      return {
        ...base,
        type: "fill",
        paint: {
          "fill-color": "#0078ff",
          "fill-opacity": 0.3,
        },
      };
    case "heatmap":
      return {
        ...base,
        type: "circle",
        paint: {
          "circle-color": "#0078ff",
          "circle-opacity": 0.2,
          "circle-radius": 6,
        },
      };
    case "background":
    case "raster":
    case "hillshade":
      // These don't have feature geometry to highlight
      return null;
    default:
      return null;
  }
}
